export type UploadKind = 'image' | 'video'

export const UploadRejectReason = {
  UnsupportedType: 'unsupported_type',
  VideoFormat: 'video_format',
  ImageTooLarge: 'image_too_large',
  BatchLimit: 'batch_limit',
  HeicUnsupported: 'heic_unsupported',
} as const
export type UploadRejectReason = typeof UploadRejectReason[keyof typeof UploadRejectReason]

const IMAGE_MIMES = ['image/jpeg', 'image/png', 'image/webp']
const VIDEO_MIMES = ['video/mp4', 'video/webm']
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp']
const VIDEO_EXTENSIONS = ['mp4', 'webm']
const HEIC_EXTENSIONS = ['heic', 'heif']
const MOV_EXTENSIONS = ['mov']

function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.')
  return dot === -1 ? '' : name.slice(dot + 1).toLowerCase()
}

export type FileClassification =
  | { ok: true; kind: UploadKind; mime: string; needsConversion: boolean }
  | { ok: false; reason: UploadRejectReason }

/** Decides by MIME type and extension whether a dropped file can be uploaded. */
export function classifyFile(file: { name: string; type: string }): FileClassification {
  const ext = extensionOf(file.name)
  const type = file.type.toLowerCase()

  if (type === 'image/heic' || type === 'image/heif' || HEIC_EXTENSIONS.includes(ext)) {
    return { ok: true, kind: 'image', mime: 'image/jpeg', needsConversion: true }
  }
  if (type === 'video/quicktime' || MOV_EXTENSIONS.includes(ext)) {
    return { ok: false, reason: UploadRejectReason.VideoFormat }
  }
  const typeMissing = type === ''
  if ((IMAGE_MIMES.includes(type) || typeMissing) && IMAGE_EXTENSIONS.includes(ext)) {
    return { ok: true, kind: 'image', mime: typeMissing ? mimeForExtension(ext) : type, needsConversion: false }
  }
  if ((VIDEO_MIMES.includes(type) || typeMissing) && VIDEO_EXTENSIONS.includes(ext)) {
    const fallback = ext === 'webm' ? 'video/webm' : 'video/mp4'
    return { ok: true, kind: 'video', mime: typeMissing ? fallback : type, needsConversion: false }
  }
  return { ok: false, reason: UploadRejectReason.UnsupportedType }
}

function mimeForExtension(ext: string): string {
  if (ext === 'png') return 'image/png'
  if (ext === 'webp') return 'image/webp'
  return 'image/jpeg'
}

export interface PlanInput {
  id: string
  bytes: number
  kind: UploadKind
}

export interface UploadPlan {
  /** Ids that fit in the remaining storage, in drop order. */
  fits: string[]
  /** Ids that do not fit in the remaining storage. */
  doesNotFit: string[]
  /** Ids rejected regardless of storage (for example images above the per-file limit). */
  rejected: Array<{ id: string; reason: UploadRejectReason }>
  /** Bytes the fitting files will use. */
  fitBytes: number
  remainingBytes: number
}

/**
 * Pre-upload quota fit check on prepared sizes. Files are taken in drop order;
 * a file that would overflow the remaining storage is skipped so smaller files
 * after it can still fit ("9 of 14 files fit").
 */
export function planUploads(
  inputs: PlanInput[],
  remainingBytes: number,
  imageMaxBytes: number,
): UploadPlan {
  const plan: UploadPlan = { fits: [], doesNotFit: [], rejected: [], fitBytes: 0, remainingBytes }
  let left = Math.max(0, remainingBytes)

  for (const input of inputs) {
    if (input.kind === 'image' && input.bytes > imageMaxBytes) {
      plan.rejected.push({ id: input.id, reason: UploadRejectReason.ImageTooLarge })
      continue
    }
    if (input.bytes <= left) {
      plan.fits.push(input.id)
      plan.fitBytes += input.bytes
      left -= input.bytes
    } else {
      plan.doesNotFit.push(input.id)
    }
  }
  return plan
}
